import React from 'react';
import { useColorModeValue } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { formatDynamicDecimal } from '../../../utils/common/common-utils';
import { deriveGlobalUiMode } from '../../../utils/common/deriveGlobalUiMode';
import { findNextSchedule } from '../../../utils/common/time-utils';
import { selectIsSmartAmpCapable, selectSite } from '../../site/siteSlice';
import {
  selectFirmwareUpdateStatus,
  selectHasReceivedSseEvent,
  selectMeterValues,
} from '../../smart-amp/evseStreamSlice';
import { useSmartAmpSse } from '../../smart-amp/useSmartAmpSse';
import { useTariffs } from '../../tariff/tariffApi';
import { selectEvChargerSchedules } from '../schedules/schedulerSlice';
import { AvailableBannerContent } from './banner-content/AvailableBannerContent';
import {
  COLOR_AMBER,
  COLOR_AMBER_DARK,
  COLOR_BLUE,
  COLOR_BLUE_DARK,
  COLOR_GRAY,
  COLOR_GREEN,
  COLOR_GREEN_DARK,
  COLOR_RED,
} from './banner-content/banner-colors';
import { BannerContentTransition } from './banner-content/BannerContentTransition';
import { BannerWrapper } from './banner-content/BannerWrapper';
import { ChargingBannerContent } from './banner-content/ChargingBannerContent';
import { ErrorBannerContent } from './banner-content/ErrorBannerContent';
import { EvseSmartAmpStoppedContent } from './banner-content/EvseSmartAmpStoppedContent';
import { FirmwareUpdateBannerContent } from './banner-content/FirmwareUpdateBannerContent';
import { GettingReadyBannerContent } from './banner-content/GettingReadyBannerContent';
import { OfflineBannerContent } from './banner-content/OfflineBannerContent';
import { ScheduledBannerContent } from './banner-content/ScheduledBannerContent';
import {
  deriveEvseDeviceActive,
  selectEvChargerData,
  selectEvseType,
  selectTariffSimulationActive,
} from './evChargerSlice';

/**
 * Converts a currency code (e.g. "USD", "uSD") or already-resolved symbol (e.g. "$")
 * to the narrow currency symbol via Intl. Falls back to the raw value on failure.
 */
function resolveCurrencySymbol(currency: string): string {
  try {
    const parts = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

export const MultipurposeBanner = ({ isWaitingForActivation = false }: { isWaitingForActivation?: boolean }) => {
  const {
    status,
    power,
    energy_consumed: energyConsumed,
    is_scheduled_charge_active: isScheduledActive,
    scheduled_charge_start_time: scheduledStart,
    scheduled_charge_end_time: scheduledEnd,
    isLoaded,
    chargeSimulationType,
  } = useSelector(selectEvChargerData);
  const { schedules } = useSelector(selectEvChargerSchedules);
  const site = useSelector(selectSite);
  const { tariffs } = useTariffs();
  const { loadActive, tempActive } = useSmartAmpSse();
  const { amountUsage, currency, kwhConsumed, kwInstantaneousPower } = useSelector(selectMeterValues);
  const hasReceivedSseEvent = useSelector(selectHasReceivedSseEvent);
  const tariffSimulationActive = useSelector(selectTariffSimulationActive);
  const evseType = useSelector(selectEvseType);
  const isSmartAmpCapableFromSite = useSelector(selectIsSmartAmpCapable);
  const { firmwareUpdateStatus, cloudFirmwareStatus } = useSelector(selectFirmwareUpdateStatus);
  const isSimulating = chargeSimulationType !== 'None';
  const isSmartAmpCapable = isSimulating || isSmartAmpCapableFromSite;
  // In simulation the SSE stream is not running, so detect it from the simulation type.
  const { isSmartAmpActive, isSmartPlugActive } = deriveEvseDeviceActive(
    evseType,
    chargeSimulationType,
    loadActive,
    tempActive
  );
  const activeSchedules = schedules.filter((s) => !s.isEditing);
  const isTimedScheduleEnabled = activeSchedules.length > 0;
  const firstActiveSchedule = activeSchedules[0] ?? null;
  const nearestSchedule = findNextSchedule(activeSchedules, site.timezone || 'UTC');
  const scheduleStartTime = nearestSchedule?.startTime || firstActiveSchedule?.startTime || scheduledStart || null;
  const scheduleEndTime = nearestSchedule?.endTime || firstActiveSchedule?.endTime || scheduledEnd || null;

  // Active firmware update states that replace normal banner content.
  // Use cloudFirmwareStatus (high-level aggregate) to gate the banner:
  // both 'Pending' and 'InProgress' mean the update is in progress.
  const isFirmwareUpdateActive = cloudFirmwareStatus === 'Pending' || cloudFirmwareStatus === 'InProgress';

  const hasTariff = isSimulating ? tariffSimulationActive : tariffs.length > 0;
  const rate: string | undefined =
    amountUsage != null && currency != null && currency !== 'N/A'
      ? `${resolveCurrencySymbol(currency)}${formatDynamicDecimal(amountUsage)}`
      : undefined;
  const energyFormatted = formatDynamicDecimal(energyConsumed);
  const effectivePower = kwInstantaneousPower ?? power;
  const powerFormatted = formatDynamicDecimal(Math.abs(effectivePower ?? 0));

  const mode = deriveGlobalUiMode(
    status,
    isScheduledActive,
    isTimedScheduleEnabled,
    scheduleStartTime,
    scheduleEndTime,
    isSmartAmpActive,
    isSmartPlugActive,
    cloudFirmwareStatus,
    firmwareUpdateStatus
  );
  const blue = useColorModeValue(COLOR_BLUE, COLOR_BLUE_DARK);
  const green = useColorModeValue(COLOR_GREEN, COLOR_GREEN_DARK);
  const amber = useColorModeValue(COLOR_AMBER, COLOR_AMBER_DARK);
  const borderColor = (() => {
    if (isWaitingForActivation) return COLOR_GRAY;

    switch (mode) {
      case 'offline':
        return COLOR_GRAY;
      case 'fw-pending':
      case 'fw-downloading':
      case 'fw-installing':
        return amber;
      case 'available':
      case 'scheduled-active':
      case 'ev-suspended':
        return green;
      case 'scheduled-waiting':
      case 'charging':
      case 'evse-suspended':
      case 'evse-smartamp-stopped':
        return blue;
      case 'error':
      case 'evse-smartplug-stopped':
        return COLOR_RED;
      default:
        return COLOR_GRAY;
    }
  })();

  function renderContent() {
    if (isFirmwareUpdateActive && cloudFirmwareStatus !== null) {
      return <FirmwareUpdateBannerContent cloudFirmwareStatus={cloudFirmwareStatus} />;
    }
    if (isWaitingForActivation) {
      return <GettingReadyBannerContent />;
    }
    switch (mode) {
      case 'offline':
        return <OfflineBannerContent />;
      case 'available':
        return (
          <AvailableBannerContent
            hasTariff={hasTariff}
            rate={rate}
            kwhConsumed={kwhConsumed}
            powerFormatted={powerFormatted}
          />
        );
      case 'scheduled-active':
      case 'scheduled-waiting':
        return <ScheduledBannerContent startTime={scheduleStartTime} endTime={scheduleEndTime} />;
      case 'charging':
        return (
          <ChargingBannerContent
            hasTariff={hasTariff}
            rate={rate}
            kwhConsumed={kwhConsumed}
            powerFormatted={powerFormatted}
          />
        );
      case 'ev-suspended':
      case 'evse-suspended':
        return <ChargingBannerContent hasTariff={hasTariff} rate={rate} kwhConsumed={kwhConsumed} powerFormatted="0" />;
      case 'evse-smartamp-stopped':
      case 'evse-smartplug-stopped':
        return <EvseSmartAmpStoppedContent powerFormatted="0" evseType={evseType} />;
      case 'error':
        return <ErrorBannerContent energy={energyFormatted} />;
    }
  }

  // Show skeleton until base status data is loaded (now driven primarily by SSE status events)
  // and, when actively charging, until the first SSE MeterValues event arrives.
  // MeterValues carries live power/energy/cost; in simulation mode SSE is not running,
  // so skip the SSE-event check.
  const needsSseData = mode === 'charging';
  const isBannerLoaded = isLoaded && (isSimulating || !needsSseData || hasReceivedSseEvent);

  // Stable identity for the currently shown content. The crossfade only fires when
  // this changes — live power/cost updates within the same mode must not re-animate.
  const contentKey = isWaitingForActivation ? 'getting-ready' : mode;

  return (
    <BannerWrapper
      isLoaded={
        isBannerLoaded ||
        isWaitingForActivation ||
        isFirmwareUpdateActive ||
        (site.commissioning_status === 'PENDING' && mode === 'offline')
      }
      evseType={evseType}
      isDeviceActive={isSmartAmpActive || isSmartPlugActive}
      isDeviceCapable={isSmartAmpCapable}
      borderColor={borderColor}
      pulseAmber={isFirmwareUpdateActive}
      pulseBlue={mode === 'charging'}
      pulseBlueGreen={mode === 'ev-suspended'}
      isDeviceStopped={mode === 'evse-smartamp-stopped' || mode === 'evse-smartplug-stopped'}
    >
      <BannerContentTransition contentKey={contentKey}>{renderContent()}</BannerContentTransition>
    </BannerWrapper>
  );
};
